import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import cn from 'classnames';

import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';

import type { CuratorReportCourseProgressChart } from '../../SchoolCuratorReportPage.helpers';
import {
  getProgressChartLineData,
  getProgressChartTabLabel,
  hasProgressChartData,
} from './CuratorReportProgressChart.helpers';

import styles from './CuratorReportProgressChart.module.scss';

type LineTooltipProps = {
  point?: {
    data?: {
      x?: number | string;
      y?: number | string;
    };
  };
};

const ChartTooltip = ({ point }: LineTooltipProps) => {
  const progress = Number(point?.data?.x ?? 0);
  const count = Number(point?.data?.y ?? 0);

  return (
    <div className={styles.tooltip}>
      <Typography className={styles.tooltipText} variant="caption" mode="compact">
        {getTranslation('school_curator_report_page.progress_chart.tooltip', {
          progress,
          count,
        })}
      </Typography>
    </div>
  );
};

const CuratorReportProgressChart = ({
  courseProgressCharts,
}: {
  courseProgressCharts: CuratorReportCourseProgressChart[];
}) => {
  const [selectedCourseId, setSelectedCourseId] = useState(
    courseProgressCharts[0]?.id ?? 'all',
  );

  useEffect(() => {
    if (
      courseProgressCharts.length &&
      !courseProgressCharts.some((chart) => chart.id === selectedCourseId)
    ) {
      setSelectedCourseId(courseProgressCharts[0].id);
    }
  }, [courseProgressCharts, selectedCourseId]);

  const selectedChart = useMemo(
    () =>
      courseProgressCharts.find((chart) => chart.id === selectedCourseId) ??
      courseProgressCharts[0],
    [courseProgressCharts, selectedCourseId],
  );

  if (!courseProgressCharts.length || !selectedChart) {
    return null;
  }

  const lineData = getProgressChartLineData(selectedChart);
  const hasData = hasProgressChartData(selectedChart);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Typography
          className={styles.title}
          variant="body1"
          weight="medium"
          mode="compact"
        >
          {getTranslation('school_curator_report_page.progress_chart.title')}
        </Typography>
        <Typography className={styles.subtitle} variant="caption" mode="compact">
          {getTranslation('school_curator_report_page.progress_chart.subtitle')}
        </Typography>
      </div>

      <div className={styles.tabsWrap}>
        <div className={styles.tabs}>
          {courseProgressCharts.map((chart) => (
            <button
              key={chart.id}
              type="button"
              className={cn(styles.tab, {
                [styles.tabActive]: chart.id === selectedCourseId,
              })}
              onClick={() => setSelectedCourseId(chart.id)}
            >
              <Typography
                className={styles.tabText}
                variant="body2"
                weight="medium"
                mode="compact"
              >
                {getProgressChartTabLabel(chart)}
              </Typography>
            </button>
          ))}
        </div>
      </div>

      {hasData ? (
        <div className={styles.chart}>
          <ResponsiveLine
            data={lineData}
            margin={{ top: 16, right: 24, bottom: 48, left: 56 }}
            xScale={{ type: 'linear', min: 0, max: 100 }}
            yScale={{ type: 'linear', min: 0, nice: true }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
              tickValues: [0, 20, 40, 60, 80, 100],
              format: (value) => `${value}%`,
              legend: getTranslation(
                'school_curator_report_page.progress_chart.axis_progress',
              ),
              legendOffset: 40,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              legend: getTranslation(
                'school_curator_report_page.progress_chart.axis_students',
              ),
              legendOffset: -48,
              legendPosition: 'middle',
            }}
            colors={['#1182f6']}
            lineWidth={3}
            pointSize={8}
            pointColor="#1182f6"
            pointBorderWidth={2}
            pointBorderColor="#ffffff"
            enableArea
            areaOpacity={0.08}
            useMesh
            enableGridX={false}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: '#889ab1',
                    fontSize: 12,
                  },
                },
                legend: {
                  text: {
                    fill: '#5c738c',
                    fontSize: 12,
                    fontWeight: 500,
                  },
                },
              },
              grid: {
                line: {
                  stroke: '#eef3fa',
                  strokeWidth: 1,
                },
              },
            }}
            tooltip={ChartTooltip as any}
          />
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Typography
            className={styles.emptyStateText}
            variant="body3"
            mode="compact"
          >
            {getTranslation('school_curator_report_page.progress_chart.empty')}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default React.memo(CuratorReportProgressChart);
